import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Search, Star, ArrowUpDown } from 'lucide-react';
import { ETH_COLORS } from './ETHLayout';
import { getETHTokens, ETHTokenData } from '../../utils/supabase';
import { getRelayTokenPriceEth } from '../../hooks/useRelayPrice';
import TradeModal from '../../components/shared/modals/TradeModal';

// ============================================
// ETH TRADE PAGE
// Full token list view with ETH-specific styling
// ============================================

// Constants
const DEFAULT_TOKEN_SUPPLY = 1_000_000_000; // 1 billion (default for factory tokens)

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  padding: 0;
  background: transparent;
  min-height: calc(100vh - 180px);
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

// ============ SEARCH & FILTERS ============
const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  position: relative;
  
  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 48px;
  background: transparent;
  border: 1px solid ${ETH_COLORS.borderLight};
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 150ms ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    border-color: ${ETH_COLORS.border};
    box-shadow: 0 0 0 3px rgba(57, 98, 116, 0.15);
  }
`;

const CreateButton = styled.button`
  padding: 14px 28px;
  background: ${ETH_COLORS.accentGradient};
  border: none;
  border-radius: 10px;
  color: ${ETH_COLORS.accent};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(57, 98, 116, 0.4);
  }
`;

// ============ TOKEN LIST ============
const TokenListContainer = styled.div`
  background: transparent;
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 16px;
  overflow: hidden;
`;

const TokenListHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 140px;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(57, 98, 116, 0.1);
  border-bottom: 1px solid ${ETH_COLORS.borderLight};
  
  @media (max-width: 900px) {
    grid-template-columns: 2fr 1fr 1fr 100px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 2fr 1fr 80px;
  }
`;

const HeaderCell = styled.div<{ $align?: 'left' | 'right' }>`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: ${props => props.$align || 'left'};
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 900px) {
    &:nth-child(4) {
      display: none;
    }
  }
  
  @media (max-width: 640px) {
    &:nth-child(3) {
      display: none;
    }
  }
`;

const TokenRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 140px;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(57, 98, 116, 0.1);
  transition: all 0.15s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(57, 98, 116, 0.1);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 900px) {
    grid-template-columns: 2fr 1fr 1fr 100px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 2fr 1fr 80px;
  }
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TokenLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid ${ETH_COLORS.borderLight};
`;

const TokenDetails = styled.div`
  min-width: 0;
`;

const TokenName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: ${ETH_COLORS.lightBlue};
`;

const TokenCell = styled.div<{ $align?: 'left' | 'right'; $positive?: boolean; $negative?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.$align === 'right' ? 'flex-end' : 'flex-start'};
  font-size: 14px;
  font-weight: 500;
  color: ${props => {
    if (props.$positive) return '#10b981';
    if (props.$negative) return '#ef4444';
    return '#ffffff';
  }};
  
  @media (max-width: 900px) {
    &:nth-child(4) {
      display: none;
    }
  }
  
  @media (max-width: 640px) {
    &:nth-child(3) {
      display: none;
    }
  }
`;

const TradeButton = styled.button`
  padding: 8px 16px;
  background: ${ETH_COLORS.accentGradient};
  border: none;
  border-radius: 6px;
  color: ${ETH_COLORS.accent};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(57, 98, 116, 0.4);
  }
`;

const FavoriteButton = styled.button<{ $active?: boolean }>`
  padding: 8px;
  background: transparent;
  border: 1px solid ${props => props.$active 
    ? ETH_COLORS.border
    : ETH_COLORS.borderLight};
  border-radius: 6px;
  color: ${props => props.$active ? ETH_COLORS.lightBlue : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.15);
    color: ${ETH_COLORS.lightBlue};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

// ============ PAGINATION ============
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid ${ETH_COLORS.borderLight};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  background: ${props => props.$active 
    ? 'rgba(57, 98, 116, 0.2)' 
    : 'transparent'};
  border: 1px solid ${props => props.$active 
    ? ETH_COLORS.border
    : ETH_COLORS.borderLight};
  border-radius: 6px;
  color: ${props => props.$active ? ETH_COLORS.lightBlue : 'rgba(255, 255, 255, 0.5)'};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.15);
    color: ${ETH_COLORS.lightBlue};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// ============ EMPTY STATE ============
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.4);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
`;

// ============ COMPONENT ============
const ETHTradePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [tokens, setTokens] = useState<ETHTokenData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  
  // Token prices cache
  const [tokenPrices, setTokenPrices] = useState<Map<string, number>>(new Map());
  
  // Trade modal state
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<{
    address: string;
    symbol: string;
    name: string;
    chainId: number;
    logo: string | null;
    decimals: number;
  } | null>(null);
  
  // Fetch tokens from Supabase
  useEffect(() => {
    const fetchTokens = async () => {
      setIsLoading(true);
      try {
        const data = await getETHTokens({
          orderBy: 'created_at',
          ascending: false,
          limit: 100
        });
        
        setTokens(data);
        
        // Fetch prices for all tokens
        const pricesMap = new Map<string, number>();
        for (const token of data) {
          if (token.contract_address) {
            try {
              const price = await getRelayTokenPriceEth(token.contract_address);
              if (price) {
                pricesMap.set(token.contract_address.toLowerCase(), price);
              }
            } catch (e) {
              console.warn(`[ETHTrade] Failed to fetch price for ${token.symbol}`);
            }
          }
        }
        setTokenPrices(pricesMap);
        
      } catch (error) {
        console.error('Error fetching ETH tokens:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTokens();
  }, []);
  
  // Format market cap
  const formatMarketCap = (value: number): string => {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  };
  
  // Format price with subscript notation for small values
  const formatPrice = (price: number | null): string => {
    if (!price) return '--';
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.0001) return `$${price.toFixed(6)}`;
    
    // For very small prices, use subscript notation
    const priceStr = price.toFixed(20);
    const match = priceStr.match(/^0\.0*([1-9])/);
    if (match) {
      const zeros = priceStr.indexOf(match[1]) - 2;
      const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
      return `$0.0₍${zeros}₎${significantDigits}`;
    }
    return `$${price.toFixed(8)}`;
  };
  
  // Get market cap for a token
  const getTokenMarketCap = (token: ETHTokenData): string => {
    const price = tokenPrices.get(token.contract_address?.toLowerCase() || '');
    if (!price) return '--';
    const marketCap = price * DEFAULT_TOKEN_SUPPLY;
    return formatMarketCap(marketCap);
  };
  
  // Get price for a token
  const getTokenPrice = (token: ETHTokenData): string => {
    const price = tokenPrices.get(token.contract_address?.toLowerCase() || '');
    return formatPrice(price ?? null);
  };
  
  const filteredTokens = tokens.filter(token => 
    token.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleToggleFavorite = (tokenId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(tokenId)) {
        newFavorites.delete(tokenId);
      } else {
        newFavorites.add(tokenId);
      }
      return newFavorites;
    });
  };
  
  // Handle trade button - open swap modal
  const handleTrade = (token: ETHTokenData, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedToken({
      address: token.contract_address || '',
      symbol: token.symbol,
      name: token.name,
      chainId: 1, // Ethereum mainnet
      logo: token.logo_url || null,
      decimals: 18,
    });
    setTradeModalOpen(true);
  };
  
  // Handle token click - open Dextools page
  const handleTokenClick = (token: ETHTokenData) => {
    // Use pool address if available, otherwise use token address
    const dextoolsUrl = token.pool_address 
      ? `https://www.dextools.io/app/en/ether/pair-explorer/${token.pool_address}`
      : `https://www.dextools.io/app/en/ether/token/${token.contract_address}`;
    window.open(dextoolsUrl, '_blank');
  };
  
  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader>
        <PageTitle>Trade Tokens</PageTitle>
        <PageSubtitle>Browse and trade all tokens deployed on Mavrk on ETH.</PageSubtitle>
      </PageHeader>
      
      {/* Search and Create */}
      <SearchSection>
        <SearchInputWrapper>
          <Search />
          <SearchInput 
            placeholder="Search by name, symbol, or contract address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchInputWrapper>
        <CreateButton onClick={() => navigate('/app/eth/create')}>
          + Create Token
        </CreateButton>
      </SearchSection>
      
      {/* Token List */}
      <TokenListContainer>
        <TokenListHeader>
          <HeaderCell>
            Token <ArrowUpDown />
          </HeaderCell>
          <HeaderCell $align="right">
            Price <ArrowUpDown />
          </HeaderCell>
          <HeaderCell $align="right">
            24h <ArrowUpDown />
          </HeaderCell>
          <HeaderCell $align="right">
            Market Cap <ArrowUpDown />
          </HeaderCell>
          <HeaderCell $align="right">
            Actions
          </HeaderCell>
        </TokenListHeader>
        
        {isLoading ? (
          <EmptyState>
            <Search />
            <p>Loading tokens...</p>
          </EmptyState>
        ) : filteredTokens.length > 0 ? (
          filteredTokens.map((token) => (
            <TokenRow key={token.id || token.contract_address} onClick={() => handleTokenClick(token)}>
              <TokenInfo>
                <TokenLogo 
                  src={token.logo_url || '/token-placeholder.png'} 
                  alt={token.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                />
                <TokenDetails>
                  <TokenName>{token.name}</TokenName>
                  <TokenSymbol>${token.symbol}</TokenSymbol>
                </TokenDetails>
              </TokenInfo>
              <TokenCell $align="right">{getTokenPrice(token)}</TokenCell>
              <TokenCell 
                $align="right" 
                $positive={true}
              >
                +0.00%
              </TokenCell>
              <TokenCell $align="right">{getTokenMarketCap(token)}</TokenCell>
              <ActionButtons>
                <TradeButton onClick={(e) => handleTrade(token, e)}>
                  Trade
                </TradeButton>
                <FavoriteButton 
                  $active={favorites.has(token.id || '')}
                  onClick={(e) => handleToggleFavorite(token.id || '', e)}
                >
                  <Star fill={favorites.has(token.id || '') ? ETH_COLORS.lightBlue : 'none'} />
                </FavoriteButton>
              </ActionButtons>
            </TokenRow>
          ))
        ) : (
          <EmptyState>
            <Search />
            <p>{searchQuery ? 'No tokens found matching your search' : 'No tokens deployed yet. Be the first!'}</p>
          </EmptyState>
        )}
        
        {/* Pagination */}
        <Pagination>
          <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
            Previous
          </PageButton>
          <PageButton $active={currentPage === 1} onClick={() => setCurrentPage(1)}>1</PageButton>
          <PageButton $active={currentPage === 2} onClick={() => setCurrentPage(2)}>2</PageButton>
          <PageButton $active={currentPage === 3} onClick={() => setCurrentPage(3)}>3</PageButton>
          <PageButton onClick={() => setCurrentPage(p => p + 1)}>
            Next
          </PageButton>
        </Pagination>
      </TokenListContainer>
      
      {/* Trade Modal */}
      <TradeModal
        isOpen={tradeModalOpen}
        onClose={() => {
          setTradeModalOpen(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
      />
    </PageContainer>
  );
};

export default ETHTradePage;
